import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FeedId, SubscriberMid, UsersBroadcastingState } from 'features/streaming/types';
import { RootState } from 'store/store';
import {
  PublishingLayer,
  ReceivingMidFields,
  ReceivingStats,
  StatsTickPayload,
  SystemStatsState,
} from 'features/system-stats/types';
import { Rid } from 'utils/webrtc';
import { UserId } from 'features/users/types';
import {
  selectAllBroadcasters,
  selectFeedIdByUser,
  selectLocalAudioBroadcasting,
  selectLocalFeedId,
  selectLocalScreenshareAudioBroadcasting,
  selectLocalScreenshareFeedId,
} from 'features/streaming/streamingSlice';
import { selectLocalUserId } from 'features/users/usersSlice';
import { ConnectionQualityLevel } from 'utils/webrtc/ConnectionQuality';
import { authorizeUser } from 'features/room/thunks/authorizeUser';
import { signalingRoomJoined } from 'features/room/actions';
import { RtcStatsServer } from 'features/room/types';
import { NetworkTotals } from 'utils/webrtc/stats/types';
import { bpsToKbps, shortCodec } from 'features/system-stats/utils';

export const initialState: SystemStatsState = {
  connectionQualityLevel: null,
  weakConnectionActive: false,
  indicatorAverageMOS: null,
  receivingFields: {
    byMid: {},
    totalBandwidthBps: 0,
  },
  connectionQualityV2Enabled: true,
  rtcStatsServer: null,
  trackScores: [],
  publishingMOSWorst: null,
  receivingMOSWorst: null,
  publishingVideoMOS: null,
  aggregateMOS: null,
  receivingMOSMedian: null,
  aggregateMOSMedianReceiving: null,
  networkTotals: { sending: null, receiving: null },
  legacyPublishingMOS: null,
  publishingLayers: {} as Record<Rid, PublishingLayer>,
};

export const systemStatsSlice = createSlice({
  name: 'systemStats',
  initialState,
  reducers: {
    connectionQualityLevelChanged(state, action: PayloadAction<ConnectionQualityLevel | null>) {
      state.connectionQualityLevel = action.payload;
    },
    weakConnectionActiveChanged(state, action: PayloadAction<boolean>) {
      state.weakConnectionActive = action.payload;
    },
    indicatorAverageMOSChanged(state, action: PayloadAction<number | null>) {
      state.indicatorAverageMOS = action.payload;
    },
    statsTickPublished(state, action: PayloadAction<StatsTickPayload>) {
      state.trackScores = action.payload.trackScores;
      state.publishingMOSWorst = action.payload.aggregate.worstPublishing;
      state.receivingMOSWorst = action.payload.aggregate.worstReceiving;
      state.publishingVideoMOS = action.payload.aggregate.publishingVideoMOS;
      state.aggregateMOS = action.payload.aggregate.aggregateMOS;
      state.receivingMOSMedian = action.payload.aggregate.medianReceiving;
      state.aggregateMOSMedianReceiving = action.payload.aggregate.aggregateMOSMedianReceiving;
      state.networkTotals = action.payload.networkTotals;
      state.legacyPublishingMOS = action.payload.legacyPublishingMOS;
      state.publishingLayers = action.payload.publishingLayers;
      state.receivingFields = action.payload.receivingFields;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(authorizeUser.fulfilled, (state, action) => {
      const flag = action.payload.settings.connectionQualityV2Enabled;
      if (flag !== undefined) {
        state.connectionQualityV2Enabled = flag;
      }
    });
    // Adopt only when present so a breakout re-join that omits it can't wipe the endpoint.
    builder.addCase(signalingRoomJoined, (state, action) => {
      if (action.payload.rtcStatsServer) {
        state.rtcStatsServer = action.payload.rtcStatsServer;
      }
    });
  },
});

export const {
  connectionQualityLevelChanged,
  weakConnectionActiveChanged,
  indicatorAverageMOSChanged,
  statsTickPublished,
} = systemStatsSlice.actions;

export const selectReceivingStatsByUser = (
  state: RootState,
  userId: UserId,
  sourceFeedId?: FeedId
): ReceivingStats => {
  const lookupFeedId = selectFeedIdByUser(state, userId);
  const feedId = sourceFeedId || lookupFeedId;
  const result: ReceivingStats = {};

  for (const meta of Object.values(state.systemStats.receivingFields.byMid)) {
    if (meta.feedId === feedId) {
      if (meta.kind === 'audio') {
        result.audioBitrate = bpsToKbps(meta.bitrateBps);
      } else {
        result.videoBitrate = bpsToKbps(meta.bitrateBps);
        result.fps = meta.framesPerSecond;
        result.codec = shortCodec(meta.codec);
        result.videoWidth = meta.frameWidth;
        result.videoHeight = meta.frameHeight;
      }
    }
  }

  return result;
};

export const selectReceivingTotalBandwidth = (state: RootState): number =>
  bpsToKbps(state.systemStats.receivingFields.totalBandwidthBps);

export const selectReceivingFieldsByMid = (
  state: RootState,
  mid: SubscriberMid
): ReceivingMidFields | undefined => state.systemStats.receivingFields.byMid[mid];

export const selectConnectionQualityLevel = (state: RootState) =>
  state.systemStats.connectionQualityLevel;

export const selectWeakConnectionActive = (state: RootState) =>
  state.systemStats.weakConnectionActive;

export const selectIndicatorAverageMOS = (state: RootState) =>
  state.systemStats.indicatorAverageMOS;

export const selectConnectionQualityV2Enabled = (state: RootState) =>
  state.systemStats.connectionQualityV2Enabled;

export const selectRtcStatsServer = (state: RootState): RtcStatsServer | null =>
  state.systemStats.rtcStatsServer;

export const selectTrackScores = (state: RootState) => state.systemStats.trackScores;

export const selectPublishingMOSWorst = (state: RootState) => state.systemStats.publishingMOSWorst;

export const selectReceivingMOSWorst = (state: RootState) => state.systemStats.receivingMOSWorst;

export const selectAggregateMOS = (state: RootState) => state.systemStats.aggregateMOS;

export const selectReceivingMOSMedian = (state: RootState) => state.systemStats.receivingMOSMedian;

export const selectAggregateMOSMedianReceiving = (state: RootState) =>
  state.systemStats.aggregateMOSMedianReceiving;

export const selectPublishingVideoMOS = (state: RootState) => state.systemStats.publishingVideoMOS;

export const selectNetworkTotals = (state: RootState): NetworkTotals =>
  state.systemStats.networkTotals;

export const selectLegacyPublishingMOS = (state: RootState) =>
  state.systemStats.legacyPublishingMOS;

export const selectPublishingLayers = (state: RootState) => state.systemStats.publishingLayers;

// Not a createSelector: runs at 0.5 Hz, so memoization buys nothing and
// would need careful equality on the broadcasting/userIdByFeedId records.
export type OrchestratorInputs = {
  localUserId: UserId | null;
  localFeedId: FeedId | null;
  // Sending feedId for the ss-pub PC — SS-send tracks must not carry the camera feedId.
  localScreenshareFeedId: FeedId | null;
  sendingAudioOn: boolean;
  // ss-pub outbound audio is gated by the screenshare feed's own audio state, not the camera mic.
  sendingScreenshareAudioOn: boolean;
  broadcastingByUserId: UsersBroadcastingState;
  userIdByFeedId: Record<FeedId, UserId>;
  connectionQualityV2Enabled: boolean;
};

export const selectOrchestratorInputs = (state: RootState): OrchestratorInputs => ({
  localUserId: selectLocalUserId(state) ?? null,
  localFeedId: selectLocalFeedId(state) ?? null,
  localScreenshareFeedId: selectLocalScreenshareFeedId(state) ?? null,
  sendingAudioOn: selectLocalAudioBroadcasting(state),
  sendingScreenshareAudioOn: selectLocalScreenshareAudioBroadcasting(state),
  broadcastingByUserId: selectAllBroadcasters(state),
  userIdByFeedId: state.streaming.userIdByFeedId,
  connectionQualityV2Enabled: selectConnectionQualityV2Enabled(state),
});

export default systemStatsSlice.reducer;
